import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { ethers } from 'ethers';
import { getInjectedProvider, isMobile } from '../utils/walletDetect';
import { ensureCorrectNetwork, isCorrectChain } from '../utils/networkSwitcher';
import { CURRENT_NETWORK } from '../config/networks';
import { useNotify } from './NotificationContext';

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const notify = useNotify();

  const [injected, setInjected] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Initialize on mount: detect injected provider (do not auto-connect)
  useEffect(() => {
    const inj = getInjectedProvider();
    if (inj) setInjected(inj);

    // Attempt silent restore only if user previously connected
    const wasConnected = localStorage.getItem('xterra_connected') === '1';
    if (inj && wasConnected) {
      inj
        .request({ method: 'eth_accounts' })
        .then((accs) => {
          if (accs && accs.length) {
            setAccount(accs[0]);
            inj.request({ method: 'eth_chainId' }).then((cid) => {
              setChainId(parseInt(cid, 16));
            });
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for wallet events (only when injected exists)
  useEffect(() => {
    if (!injected || !injected.on) return;

    const onAccountsChanged = (accs) => {
      if (!accs || accs.length === 0) {
        setAccount(null);
        localStorage.removeItem('xterra_connected');
        notify.info('Wallet disconnected.');
      } else {
        setAccount(accs[0]);
      }
    };

    const onChainChanged = (hex) => {
      const cid = parseInt(hex, 16);
      setChainId(cid);
      // Do NOT reload the page.
    };

    injected.on('accountsChanged', onAccountsChanged);
    injected.on('chainChanged', onChainChanged);

    return () => {
      if (injected.removeListener) {
        injected.removeListener('accountsChanged', onAccountsChanged);
        injected.removeListener('chainChanged', onChainChanged);
      }
    };
  }, [injected, notify]);

  // Web3Provider derived from injected (fresh each chain change)
  const web3Provider = useMemo(() => {
    if (!injected) return null;
    // 'any' allows network changes without recreating the provider
    return new ethers.providers.Web3Provider(injected, 'any');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injected, chainId]);

  const signer = useMemo(() => {
    if (!web3Provider || !account) return null;
    return web3Provider.getSigner(account);
  }, [web3Provider, account]);

  async function connect() {
    if (connecting) return;
    setConnecting(true);
    try {
      const inj = injected || getInjectedProvider();
      if (!inj) {
        if (isMobile()) {
          notify.error('Please open this site inside your wallet browser (Trust Wallet, MetaMask).');
        } else {
          notify.error('No wallet detected. Please install MetaMask or Trust Wallet.');
        }
        setConnecting(false);
        return;
      }
      if (!injected) setInjected(inj);

      const accs = await inj.request({ method: 'eth_requestAccounts' });
      if (!accs || !accs.length) {
        notify.error('No account authorized.');
        setConnecting(false);
        return;
      }
      setAccount(accs[0]);
      localStorage.setItem('xterra_connected', '1');

      const cidHex = await inj.request({ method: 'eth_chainId' });
      const cid = parseInt(cidHex, 16);
      setChainId(cid);

      if (!isCorrectChain(cid)) {
        setSwitching(true);
        try {
          await ensureCorrectNetwork(inj);
          notify.success('Connected to ' + CURRENT_NETWORK.displayName);
        } catch (e) {
          notify.error(e.message || 'Could not switch network.');
        } finally {
          setSwitching(false);
        }
      } else {
        notify.success('Wallet connected.');
      }
    } catch (err) {
      if (err && err.code === 4001) {
        notify.error('Connection rejected.');
      } else {
        notify.error(err?.message || 'Failed to connect.');
      }
    } finally {
      setConnecting(false);
    }
  }

  function disconnect() {
    setAccount(null);
    localStorage.removeItem('xterra_connected');
    notify.info('Disconnected.');
  }

  async function switchNetwork() {
    if (switching) return;
    if (!injected) {
      notify.error('No wallet connected.');
      return;
    }
    setSwitching(true);
    try {
      await ensureCorrectNetwork(injected);
      notify.success('Network switched.');
    } catch (e) {
      notify.error(e.message || 'Could not switch network.');
    } finally {
      setSwitching(false);
    }
  }

  const value = {
    injected,
    web3Provider,
    signer,
    account,
    chainId,
    isConnected: !!account,
    isCorrectNetwork: isCorrectChain(chainId),
    connecting,
    switching,
    connect,
    disconnect,
    switchNetwork,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be inside WalletProvider');
  return ctx;
}
